import { FC } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Button, Input, Select } from '../../../custom';
import { useCreateSpecializationMutation } from '../../../hooks/api/specialization/useCreateSpecializationMutation';
import { Specialization } from '../../../hooks/api/specialization/useGetSpecializationQuery';
import { useUpdateSpecializationMutation } from '../../../hooks/api/specialization/useUpdateSpecializationMutation';
import { StatusOptions } from '../../../requests';
import { validator } from '../../../utils/validator';
import { getAllProgram } from '../program/request';

interface ComponentProps {
  record?: Specialization;
  handleClose: () => void;
  isEdit?: boolean;
}

const AddSpecialization: FC<ComponentProps> = ({ record, handleClose, isEdit = false }) => {
  const { createSpecializationHandler, isLoading } = useCreateSpecializationMutation(handleClose);
  const { updateSpecializationHandler, isUpdateLoading } = useUpdateSpecializationMutation(handleClose);

  const { data: programs, isLoading: isProgramsLoading } = useQuery({
    queryKey: ['specialization-programs'],
    queryFn: () => getAllProgram(1, 1000),
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Specialization name is required'),
    code: Yup.string().required('Specialization code is required'),
    description: validator.description,
    academicProgramId: isEdit ? Yup.string().notRequired() : validator.program,
    activeStatus: isEdit ? validator.status : Yup.string().notRequired(),
  });

  const programOptions = programs?.data?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.programName}
    </option>
  ));

  return (
    <Formik
      initialValues={{
        id: record?.id ?? 0,
        name: record?.name ?? '',
        code: record?.code ?? '',
        description: record?.description ?? '',
        academicProgramId: record?.academicProgramId?.toString() ?? '',
        activeStatus: record?.activeStatus?.toString() ?? '',
      }}
      onSubmit={(values, { resetForm }) => {
        if (isEdit) {
          updateSpecializationHandler(values, resetForm);
        } else {
          createSpecializationHandler(values, resetForm);
        }
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {() => (
        <Form className="fields">
          <Input name="name" placeholder="Enter specialization name" label="Name" />
          <Input name="code" placeholder="Enter specialization code" label="Code" />
          <Input name="description" placeholder="Enter description" label="Description" type="textarea" />

          <Select
            name="academicProgramId"
            placeholder="Select Academic Program"
            label="Academic Program"
            loading={isProgramsLoading}
            options={programOptions}
          />

          <Select
            name="activeStatus"
            placeholder="Select Status"
            label="Status"
            options={
              <>
                {StatusOptions.map((option: any) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            }
          />

          <div className="btn-group">
            <Button type="button" variant="text" text="Cancel" onClick={handleClose} />
            <Button
              type="submit"
              disabled={isEdit ? isUpdateLoading : isLoading}
              isLoading={isEdit ? isUpdateLoading : isLoading}
              text={isEdit ? 'Update' : 'Create'}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AddSpecialization;
